import {defineStore} from 'pinia'
import axios from '@/api/axios'
import {IMPERSONATION_TOKEN_KEY} from '@shared/constants/auth'
import {authActions} from '@shared-utils/auth/authActions'

export const useAuthenticationStore = defineStore('authenticationStore', {
  state: () => ({
    isAuthenticated: false,
    user: {},
    performanceReportAvailability: {},
  }),
  getters: {
    authUser: state => {
      return state.user
    },
    userCompanyHash: state => {
      return state.user?.company?.hash
    },
    userHasCompany: state => {
      return !!state.user?.company
    },
    isDemoCompany: state => {
      return state.user?.company?.hash === 'lbMDVBRx' || false
    },
    userUnitId: state => {
      return state.user?.unit?.id
    },
    isInternalUser: state => {
      return state.user.policies?.is_internal || false
    },
    isManager: state => {
      return !!state.user?.is_manager || false
    },
    canOpenAdAccountReport: state => {
      return state.user.policies?.can_open_ad_account_report || false
    },
    hasAccessToTopUps: state => {
      return state.user.company?.top_ups_enabled || false
    },
    isAgencyClient: state => {
      return state.user.policies?.is_agency_client || false
    },
    canUseTopUpAsap: state => {
      return state.user.policies?.asap_top_up_enabled || false
    },
    canUseInvoices: state => {
      return state.user.policies?.can_use_invoices || false
    },
    canUseRetainers: state => {
      return state.user.policies?.can_use_retainers || false
    },
    canSeeFBPageRequestForm: state => {
      return state.user.policies?.can_view_fb_page_requests_form || false
    },
    canWorkWithCreatives: state => {
      return state.user.policies?.can_work_with_creatives || false
    },
    hasTwoFA: state => {
      return state.user.two_fa_enabled || false
    },
    hasAccessToUsersPage: state => {
      return state.user.policies?.has_access_to_users_page || false
    },
    hasAccessToOverdrafts: state => {
      return state.user.policies?.has_access_to_overdrafts || false
    },
    hasAccessToRequestsPage: state => {
      return state.user.policies?.has_access_to_requests_page || false
    },
    hasAccessToProformaInvoices: state => {
      return state.user.policies?.has_access_to_proforma_invoices || false
    },
    hasAccessToAssetsPage: state => {
      return state.user.policies?.has_access_to_assets_page || false
    },
    isCompanyAdmin: state => {
      return (state.user?.company?.hash && state.user?.subrole === 'admin') || false
    },
    isSubroleMember: state => state.user?.subrole === 'member' || false,
    canUseUserFilter: getters => !getters.isSubroleMember,
    userBalanceStatus: state => state.user?.company?.balance_info?.remaining_balance_status || null,
    userRemainingBalanceDays: state => state.user?.company?.balance_info?.remaining_balance_days || null,
    userAvailableBalance: state => state.user?.company?.balance_info?.available_balance || null,
    isGoogleUser: getters => {
      return getters.authUser.email === 'google@point2web.com'
    },
    requestsRouteName: () => {
      return 'my-requests.list'
    },
    isImpersonated: () => {
      return !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
    },
    // True only when the current impersonation was started by a developer
    // (tech team, incl. Denis Bilenko). Resolved server-side in policies.
    isImpersonatedByDeveloper: state => {
      return state.user.policies?.is_impersonated_by_developer || false
    },
  },
  actions: {
    ...authActions({axios}),
    fetchPerformanceReportAvailability: async function () {
      try {
        const {data} = await axios.get('/client-dashboard/performance-reports/availability')
        this.performanceReportAvailability = data
      } catch (e) {
        console.error(e)
      }
    },
  },
})
